import { Employee, Project } from "src/app/domains/internal";

import { Contract } from "../features/customer/domains/contract";
import { Intervention } from "../features/interventions/domains/intervention";

// Lu à chaque passe CD du planning : toLocaleTimeString recrée un formateur par appel.
const HOUR_MINUTE_FORMAT = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" });

function formatHourMinute(value: Date | string): string {
  const date = new Date(value);
  return isNaN(date.getTime()) ? "Invalid Date" : HOUR_MINUTE_FORMAT.format(date);
}

export enum EmployeeActivityType {
  WORKING = "WORKING",
  ABSENCE = "ABSENCE",
  MEAL_BREAK = "MEAL_BREAK",
  SCHEDULE = "SCHEDULE",
  OTHER_ACTIVITY = "OTHER_ACTIVITY",
}
export enum EmployeeActivityTypeLabel {
  WORKING = "Réalisé",
  SCHEDULE = "Planifié",
  ABSENCE = "Absence",
  MEAL_BREAK = "Pause Repas",
  OTHER_ACTIVITY = "Autre activité",
}
export enum OtherActivitySubtype {
  DEPOSIT = "DEPOSIT",
  BAD_WEATHER = "BAD_WEATHER",
  PROPERTY_MANAGEMENT = "PROPERTY_MANAGEMENT",
}
export enum OtherActivitySubtypeLabel {
  DEPOSIT = "Dépôt",
  BAD_WEATHER = "Intempérie",
  PROPERTY_MANAGEMENT = "Régie",
}

/**
 * Libellé d'affichage d'une activité OTHER_ACTIVITY : nom du motif personnalisé si posé,
 * sinon filet de sécurité sur l'ancien sous-type legacy (DEPOSIT/BAD_WEATHER/PROPERTY_MANAGEMENT,
 * remplacés par les motifs personnalisés), sinon "Autre activité". Toutes les activités en
 * prod ont déjà un motif (EP-1017) : ce fallback ne couvre plus qu'un backup ancien restauré
 * ou un import externe qui ne poserait que le subtype.
 */
export function resolveOtherActivityLabel(
  activity: { other_activity_motif_id?: string | null; other_activity_subtype?: OtherActivitySubtype | string | null },
  motifs: ReadonlyArray<{ other_activity_motif_id: string; name: string }> | null | undefined,
): string {
  const motif = activity.other_activity_motif_id
    ? motifs?.find((m) => m.other_activity_motif_id === activity.other_activity_motif_id)
    : undefined;
  if (motif) return motif.name;
  const subtypeLabel = activity.other_activity_subtype
    ? OtherActivitySubtypeLabel[activity.other_activity_subtype as keyof typeof OtherActivitySubtypeLabel]
    : undefined;
  if (subtypeLabel) return subtypeLabel;
  return EmployeeActivityTypeLabel.OTHER_ACTIVITY;
}

/** Clé de colonne "<libellé> (h)" des exports/récaps d'heures pour une activité OTHER_ACTIVITY. */
export function otherActivityColumnLabel(
  activity: Parameters<typeof resolveOtherActivityLabel>[0],
  motifs: Parameters<typeof resolveOtherActivityLabel>[1],
): string {
  return `${resolveOtherActivityLabel(activity, motifs)} (h)`;
}
export enum EmployeeActivityStatus {
  PENDING = "PENDING",
  VALIDATED = "VALIDATED",
  REJECTED = "REJECTED",
}
export enum EmployeeActivityStatusLabel {
  PENDING = "En attente",
  VALIDATED = "Validée",
  REJECTED = "Rejetée",
}

export enum AbsenceType {
  OTHER = "OTHER",
  SICKNESS = "SICKNESS",
  VACATION = "VACATION",
  PUBLIC_HOLIDAY = "PUBLIC_HOLIDAY",
  ACCIDENT = "ACCIDENT",
  VACATION_OVERTIME = "VACATION_OVERTIME",
  JUSTIFIED = "JUSTIFIED",
  NOT_PAID = "NOT_PAID",
  TRAINING = "TRAINING",
  CARENCE_SICKNESS = "CARENCE_SICKNESS",
  CARENCE_ACCIDENT = "CARENCE_ACCIDENT",
}
export enum AbsenceTypeLabel {
  OTHER = "Autre",
  SICKNESS = "Maladie",
  VACATION = "Congés",
  PUBLIC_HOLIDAY = "Férié",
  ACCIDENT = "Accident",
  VACATION_OVERTIME = "Congés Heures Supplémentaires",
  JUSTIFIED = "Justifiées",
  NOT_PAID = "Non payés",
  TRAINING = "Formations",
  CARENCE_SICKNESS = "Carence maladie",
  CARENCE_ACCIDENT = "Carence accident",
}

export interface IncreasedHoursEntry {
  rule_id: string;
  rule_name: string;
  rate: number;
  hours: number;
}
export interface IncreasedHoursByRate {
  rate: number;
  hours: number;
}

export class EmployeeActivity {
  employee_activity_id: string;
  activity_type: EmployeeActivityType;
  start_date: Date;
  end_date: Date;
  project_id: string;
  intervention_id: string = null;
  hour_cost: number;
  absence_type: string;
  comment: string;
  absence_attachment: string;
  meal_payback_request = true;
  meal_payback_amount = 21;
  is_external: boolean;
  is_selected: boolean = false;
  activity_status: EmployeeActivityStatus = EmployeeActivityStatus.PENDING;

  manager_comment = "";
  manager_id = "";
  manager_edit_on = new Date();
  manager_attachments_ids = [];
  absence_time_selection_type: string;
  team_id: string;
  other_activity_subtype: OtherActivitySubtype = null;
  other_activity_motif_id: string = null;
  /** Snapshot pris à la saisie (cf other_activity_motif.accounting_type) : "DECLARED" | "WORKED" | "ABSENCE" | null (legacy, traité comme "WORKED"). */
  other_activity_accounting_type: string | null = null;
  service_nature_id: string | null = null;
  increase_rate_override: number | null = null;
  increased_hours?: IncreasedHoursEntry[];
  increased_hours_effective?: IncreasedHoursByRate[];
  increase_rate_effective?: number | null;

  get hours_count(): number {
    if (!this.start_date || !this.end_date) {
      return 0;
    }
    return (this.end_date.getTime() - this.start_date.getTime()) / 1000 / 3600;
  }

  get isValidated(): boolean {
    return this.activity_status === EmployeeActivityStatus.VALIDATED;
  }

  get isRejected(): boolean {
    return this.activity_status === EmployeeActivityStatus.REJECTED;
  }

  get isPending(): boolean {
    return !this.activity_status || this.activity_status === EmployeeActivityStatus.PENDING;
  }

  get displayLabel(): string {
    if (this.isValidated) return EmployeeActivityStatusLabel.VALIDATED;
    if (this.isRejected) return EmployeeActivityStatusLabel.REJECTED;
    return EmployeeActivityTypeLabel[this.activity_type] || "";
  }

  get predefinedPeriodLabel(): string {
    if (this.activity_type !== "ABSENCE") return "";
    if (this.absence_time_selection_type !== "PREDEFINED") return "";
    if (this.hours_count > 15) return "Journée";
    const startHour = new Date(this.start_date).getHours();
    return startHour < 12 ? "Matin" : "Après-midi";
  }

  get isIntervention(): boolean {
    return !!this.intervention_id;
  }

  get increased_hours_total(): number {
    const total = (this.increased_hours ?? []).reduce((sum, entry) => sum + entry.hours, 0);
    return Math.round(total * 100) / 100;
  }

  getMealBreakTooltip(evaCompanyAllows = false) {
    const type = this.meal_payback_request && evaCompanyAllows ? "Repas" : "Pause";
    const start = formatHourMinute(this.start_date);
    const end = formatHourMinute(this.end_date);
    const projectName = this.project?.name || "";

    return `${type} de ${start} à ${end} - Chantier: ${projectName}`;
  }

  get isMealBreakAllowed(): boolean {
    let is_meal_break_allowed = false;
    try {
      is_meal_break_allowed = JSON.parse(localStorage.getItem("currentEvaCompany")).is_meal_break_allowed;
    } catch (error) {
      is_meal_break_allowed = false;
      console.error(error);
    }

    return is_meal_break_allowed;
  }

  get activity_cost(): number {
    if (
      this.activity_type === EmployeeActivityType.WORKING ||
      this.activity_type === EmployeeActivityType.SCHEDULE ||
      this.activity_type === EmployeeActivityType.OTHER_ACTIVITY
    ) {
      return this.hour_cost * this.hours_count;
    } else if (this.activity_type === EmployeeActivityType.MEAL_BREAK) {
      return this.meal_payback_request && this.isMealBreakAllowed ? this.meal_payback_amount : 0;
    } else {
      return 0;
    }
  }

  // external
  project: Project = null;
  intervention: Intervention = null;
  contract: Contract = null;
  manager_attachments = [];
  manager: Employee;
  employee_id: string;
  address_str: string;
  planning_label_str: string;

  public static createInstance(jsonData: EmployeeActivity): EmployeeActivity {
    const e = Object.assign(new EmployeeActivity(), jsonData);
    e.activity_status = (jsonData as any).activity_status || EmployeeActivityStatus.PENDING;
    if (jsonData.project) {
      e.project = Project.createInstance(jsonData.project);
    }
    e.start_date = jsonData.start_date ? new Date(jsonData.start_date) : null;
    e.end_date = jsonData.end_date ? new Date(jsonData.end_date) : null;
    if (jsonData.manager) {
      e.manager = Employee.createInstance(jsonData.manager);
    }
    if (jsonData.intervention) {
      e.intervention = Intervention.createInstance(jsonData.intervention);
    }
    if (jsonData.contract) {
      e.contract = Contract.createInstance(jsonData.contract);
    }
    return e;
  }
  public static createInstances(activities: any[]): EmployeeActivity[] {
    return activities.map(EmployeeActivity.createInstance);
  }

  public static createNewActivity(startDate: Date): EmployeeActivity {
    const e = new EmployeeActivity();
    e.activity_type = EmployeeActivityType.WORKING;
    e.start_date = new Date(startDate);
    e.end_date = new Date(startDate);
    return e;
  }

  public static createSchedule(
    startDate: Date,
    endDate: Date,
    projectId: string,
    manager_comment,
    manager_id,
    intervention_id = null,
    team_id: string = "",
  ): EmployeeActivity {
    const e = new EmployeeActivity();
    e.activity_type = EmployeeActivityType.SCHEDULE;
    e.start_date = new Date(startDate);
    e.end_date = new Date(endDate);
    e.project_id = projectId;
    e.manager_comment = manager_comment;
    e.manager_id = manager_id;
    e.intervention_id = intervention_id;
    e.team_id = team_id;
    return e;
  }

  public static createMealBreak(
    startDate: Date,
    endDate: Date,
    projectId: string,
    manager_comment,
    manager_id,
    meal_payback_request,
    meal_payback_amount = 21,
    team_id: string = "",
  ): EmployeeActivity {
    const e = new EmployeeActivity();
    e.activity_type = EmployeeActivityType.MEAL_BREAK;
    e.start_date = new Date(startDate);
    e.end_date = new Date(endDate);
    e.project_id = projectId;
    e.manager_comment = manager_comment;
    e.manager_id = manager_id;
    e.meal_payback_request = meal_payback_request;
    e.meal_payback_amount = meal_payback_amount;
    e.team_id = team_id;
    return e;
  }
  public static createWorking(
    startDate: Date,
    endDate: Date,
    projectId: string,
    manager_comment,
    manager_id,
    intervention_id = null,
    team_id: string = "",
  ): EmployeeActivity {
    const e = new EmployeeActivity();
    e.activity_type = EmployeeActivityType.WORKING;
    e.start_date = new Date(startDate);
    e.end_date = new Date(endDate);
    e.project_id = projectId;
    e.manager_comment = manager_comment;
    e.manager_id = manager_id;
    e.intervention_id = intervention_id;
    e.team_id = team_id;

    return e;
  }
  public static createAbsence(
    startDate: Date,
    endDate: Date,
    absenceType,
    absencetimeSelectionType,
    manager_comment,
    manager_id,
    team_id: string = "",
  ): EmployeeActivity {
    const e = new EmployeeActivity();
    e.activity_type = EmployeeActivityType.ABSENCE;
    e.absence_type = absenceType;
    e.absence_time_selection_type = absencetimeSelectionType;
    e.start_date = new Date(startDate);
    e.end_date = new Date(endDate);
    e.manager_comment = manager_comment;
    e.manager_id = manager_id;
    e.team_id = team_id;
    return e;
  }

  public static createOtherActivity(
    startDate: Date,
    subtype: OtherActivitySubtype,
    endDate: Date,
    projectId: string = null,
    manager_comment = "",
    manager_id = "",
    team_id: string = "",
  ): EmployeeActivity {
    const e = new EmployeeActivity();
    e.activity_type = EmployeeActivityType.OTHER_ACTIVITY;
    e.start_date = new Date(startDate);
    e.end_date = new Date(endDate);
    e.other_activity_subtype = subtype;
    e.project_id = projectId;
    e.manager_comment = manager_comment;
    e.manager_id = manager_id;
    e.team_id = team_id;
    return e;
  }

  clone(): EmployeeActivity {
    const clone = EmployeeActivity.createInstance(this);
    return clone;
  }
}
